import {cn} from "@/lib/cn"

import {DizedIcon} from "./dized-icon"

/**
 * The DIZED content bar — the same one dized-next's beta2 library puts on its
 * cards and rows.
 *
 * Ported rather than invented, from `dized-next/src/components/DizedBar.tsx`
 * and `DizedIcon.tsx`, because "which of my games have Dized content" is the
 * same question a player asks on the storefront and a publisher asks here, and
 * two different answers to it would be two different products. The gradient and
 * the foreground colour were already in this repo's `app.css`
 * (`bg-dized-bar`, `--dized-bar-foreground`, copied with the rest of the design
 * system) and had no consumer until now.
 *
 * The three glyphs live in `public/icons/` and are the same files beta2 serves.
 */

const CONTENT_ICONS = [
  {key: "tutorial", label: "Tutorial"},
  {key: "rules", label: "Rules"},
  {key: "extras", label: "Extras"},
] as const

export type DizedContent = {
  readonly tutorial: boolean
  readonly rules: boolean
  readonly extras: boolean
}

/**
 * The bar itself: the DIZED label, and a glyph per content type the game has.
 *
 * Two behaviours are load-bearing and both came from beta2's own feedback
 * rounds rather than from taste:
 *
 * 1. **Only the types the game actually has are drawn.** An earlier beta2
 *    revision hardcoded all three, which badged every card with everything.
 * 2. **The height is reserved even when there is no bar.** `present={false}`
 *    renders a blank strip of the same height, so a grid of cards does not go
 *    jagged where some games have content and others do not. It is blank card
 *    background rather than a dimmed bar, because a DIZED-branded bar on a game
 *    with no Dized content is a false claim.
 */
export const DizedBar = ({
  present = true,
  content,
  className,
}: {
  present?: boolean
  content?: DizedContent
  className?: string
}) => {
  const icons = content === undefined ? CONTENT_ICONS : CONTENT_ICONS.filter(is => content[is.key])

  if (!present) {
    return <div aria-hidden className={cn("h-7", className)} />
  }

  return (
    <div
      className={cn(
        "flex h-7 items-center justify-between bg-dized-bar px-3 text-(--dized-bar-foreground)",
        className,
      )}
    >
      <span className="font-display text-xs font-bold uppercase tracking-wide">Dized</span>
      <span className="flex items-center gap-2.5">
        {icons.map(icon => (
          <DizedIcon key={icon.key} name={icon.key} label={icon.label} className="size-[13px]" />
        ))}
      </span>
    </div>
  )
}
